import { createApiResponse, createErrorApiResponse } from "@entrilia/shared/api/axiosHelper";
import { getClientCode } from "@entrilia/shared/api/clientApiContext";
import type {
  CreateMultipleObjectMetricExtensionsRequest,
  ObjectMetricExtension,
  SaveSubmissionLayoutBlockInputRequest,
  SearchObjectMetricExtensionsRequest,
} from "@entrilia/shared/api/dataCollectionTypes";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import { getDefaultHeaders } from "@entrilia/shared/api/headers";
import type { ApiResponse, PaginatedList } from "@entrilia/shared/api/types";
import type { WorkflowType } from "@entrilia/shared/api/workflowTypes";
import { getBackendBaseUrl } from "@entrilia/shared/variables";
import axios from "axios";
import qs from "qs";
import type { InboxStructureTree } from "../store/state/inboxStructure/types";
import type {
  FilterConfiguration,
  Message,
  SearchMessagesRequest,
  TranslateIdsRequest,
  TranslateIdsResponse,
} from "../store/state/messages/types";
import type { Identity, UpdateIdentityRequest, UserPermissionsResponse } from "../store/state/user/types";
import type { BlobProbeTarget } from "./types/accessCheckApiTypes";
import type {
  AssignBankAccountsRequest,
  CreateInvestorBankAccountRequest,
  InvestorBankAccountsResponse,
} from "./types/bankAccountTypes";
import type { CancelInvestorChangeRequest, ChangeRequestDetails } from "./types/changeRequestsTypes";
import type { FileInfo } from "./types/commonApiTypes";
import type {
  DataCollectionSubmissionDetails,
  DataCollectionSubmissionInfo,
  ImportSubmissionBlockInputResponse,
  SearchDataCollectionRequest,
} from "./types/dataCollectionTypes";
import type { Fundraising } from "./types/fundraisingTypes";
import type { ImpersonationOptions } from "./types/impersonationTypes";
import type {
  InvestorCommunication,
  InvestorCommunicationCategory,
  InvestorCommunicationInfo,
  SearchInvestorCommunicationsRequest,
} from "./types/investorCommunicationsTypes";
import type { InvestorContactsResponse } from "./types/investorContactsTypes";
import type { InvestorDetails, UpdateInvestorDetailsRequest } from "./types/investorDetailsTypes";
import type {
  MembersChangeRequestAttachment,
  MembersChangeRequestDeltaRequest,
  MembersChangeRequestView,
  RemoveMembersChangeRequestAttachmentRequest,
  UpsertMembersChangeRequestRequest,
} from "./types/membersChangeRequestTypes";
import type { AccessCategory, ClientDictionaries } from "./types/settingsTypes";
import type {
  BalanceReportExportRequest,
  BalanceReportParameters,
  BalanceReportRequest,
  CashFlowsReportParameters,
  CashFlowsReportRequest,
  PortfolioReportParameters,
  PortfolioReportRequest,
  TabularReport,
} from "./types/widgetTypes";

const baseUri = getBackendBaseUrl("ir-portal");

export const api = {
  user: {
    loadPermissions: async () => {
      const { data } = await axios.get<ApiResponse<UserPermissionsResponse>>(`${baseUri}/User/permissions`, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
    loadIdentity: async (largeAvatar = false) => {
      const { data } = await axios.get<ApiResponse<Identity>>(`${baseUri}/Identity`, {
        params: {
          loadLargeAvatarImage: largeAvatar,
        },
      });
      return data;
    },
    updateIdentity: async (payload: UpdateIdentityRequest) => {
      const { data } = await axios.patch<ApiResponse<Identity>>(`${baseUri}/Identity`, payload, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
    processAvatar: async (avatarImage: string) => {
      const { data } = await axios.post<ApiResponse<string>>(
        `${baseUri}/Identity/ProcessAvatar`,
        {
          avatarImage,
        },
        { headers: getDefaultHeaders() }
      );
      return data;
    },
  },
  dictionaries: {
    getDictionaries: async () => {
      const { data } = await axios.get<ApiResponse<ClientDictionaries>>(`${baseUri}/Dictionaries`, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
  },
  settings: {
    getAccessCategories: async () => {
      const { data } = await axios.get<ApiResponse<AccessCategory[]>>(
        `${baseUri}/SettingsManagement/LP/AccessCategories`,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
  },
  investorDetails: {
    getInvestorDetails: async (investorId: string) => {
      const { data } = await axios.get<ApiResponse<InvestorDetails>>(
        `${baseUri}/InvestorDetails/LP/Investors/${investorId}/details`,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    updateInvestorDetails: async (investorId: string, request: UpdateInvestorDetailsRequest) => {
      const { data } = await axios.patch<ApiResponse<ChangeRequestDetails>>(
        `${baseUri}/InvestorDetails/LP/Investors/${investorId}/details`,
        request,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
  },
  bankAccounts: {
    getBankAccounts: async (investorId: string) => {
      const { data } = await axios.get<ApiResponse<InvestorBankAccountsResponse>>(
        `${baseUri}/LP/Investors/${investorId}/bank-accounts`,
        {
          headers: getDefaultHeaders(),
        }
      );
      return data;
    },
    createBankAccount: async (investorId: string, payload: CreateInvestorBankAccountRequest) => {
      const { data } = await axios.put<ApiResponse<ChangeRequestDetails>>(
        `${baseUri}/LP/Investors/${investorId}/bank-accounts`,
        payload,
        {
          headers: getDefaultHeaders(),
        }
      );
      return data;
    },
    assignBankAccountsToFundInvestors: async (payload: AssignBankAccountsRequest) => {
      const { data } = await axios.post<ApiResponse<ChangeRequestDetails>>(
        `${baseUri}/LP/Investors/bank-account-assignments`,
        payload,
        {
          headers: getDefaultHeaders(),
        }
      );
      return data;
    },
  },
  investorContacts: {
    getInvestorContacts: async (investorId: string) => {
      const { data } = await axios.get<ApiResponse<InvestorContactsResponse>>(
        `${baseUri}/LP/Investors/${investorId}/contacts`,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
  },
  membersChangeRequests: {
    getStatusView: async (investorId: string) => {
      const { data } = await axios.get<ApiResponse<MembersChangeRequestView>>(
        `${baseUri}/LP/Investors/${investorId}/members-change-request`,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    preview: async (investorId: string, payload: MembersChangeRequestDeltaRequest) => {
      const { data } = await axios.post<ApiResponse<MembersChangeRequestView>>(
        `${baseUri}/LP/Investors/${investorId}/members-change-request/preview`,
        payload,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    upsert: async (investorId: string, payload: UpsertMembersChangeRequestRequest) => {
      const { data } = await axios.post<ApiResponse<MembersChangeRequestView>>(
        `${baseUri}/LP/Investors/${investorId}/members-change-request/upsert`,
        payload,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    uploadAttachment: async (investorId: string, file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axios.post<ApiResponse<MembersChangeRequestAttachment>>(
        `${baseUri}/LP/Investors/${investorId}/members-change-request/attachments`,
        formData,
        { headers: getDefaultHeaders({ formData: true }) }
      );
      return data;
    },
    removeAttachment: async (investorId: string, payload: RemoveMembersChangeRequestAttachmentRequest) => {
      const { data } = await axios.post<ApiResponse<void>>(
        `${baseUri}/LP/Investors/${investorId}/members-change-request/attachments/remove`,
        payload,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
  },
  changeRequests: {
    getPendingChangeRequests: async (investorId: string, workflowType: WorkflowType) => {
      const { data } = await axios.get<ApiResponse<{ changeRequestDetails: ChangeRequestDetails[] }>>(
        `${baseUri}/LP/Investors/${investorId}/pending-change-request`,
        {
          headers: getDefaultHeaders(),
          params: { workflowType },
        }
      );
      return data;
    },
    cancelChangeRequest: async (investorId: string, payload: CancelInvestorChangeRequest) => {
      const { data } = await axios.post<ApiResponse<void>>(
        `${baseUri}/LP/Investors/${investorId}/change-requests/cancel`,
        payload,
        {
          headers: getDefaultHeaders(),
        }
      );
      return data;
    },
  },
  impersonation: {
    getImpersonationOptions: async () => {
      const { data } = await axios.get<ApiResponse<ImpersonationOptions>>(`${baseUri}/Impersonation/options`);
      return data;
    },
  },
  messages: {
    search: async (payload: SearchMessagesRequest) => {
      const { data } = await axios.post<ApiResponse<PaginatedList<Message>>>(`${baseUri}/Messages/search`, payload, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
    getDetails: async (id: string) => {
      const { data } = await axios.get<ApiResponse<Message>>(`${baseUri}/Messages/${id}`, {
        params: { includeContent: true },
        headers: getDefaultHeaders(),
      });
      return data;
    },
    markAsRead: async (id: string) => {
      const { data } = await axios.patch<ApiResponse<Message>>(
        `${baseUri}/Messages/${id}`,
        {
          markAsRead: true,
        },
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    markManyAsRead: async (ids: string[], markAsRead: boolean) => {
      const { data } = await axios.patch<
        ApiResponse<{ updates: { messageId: string; markAsRead: boolean; markAsImportant: boolean }[] }>
      >(
        `${baseUri}/Messages/`,
        {
          messageIds: ids,
          markAsRead,
        },
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    markManyAsImportant: async (ids: string[], markAsImportant: boolean) => {
      const { data } = await axios.patch<
        ApiResponse<{ updates: { messageId: string; markAsRead: boolean; markAsImportant: boolean }[] }>
      >(
        `${baseUri}/Messages/`,
        {
          messageIds: ids,
          markAsImportant,
        },
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    loadFilterConfiguration: async () => {
      const { data } = await axios.get<ApiResponse<FilterConfiguration>>(`${baseUri}/Messages/search/params`, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
    translateIds: async (payload: TranslateIdsRequest) => {
      const { data } = await axios.post<ApiResponse<TranslateIdsResponse>>(
        `${baseUri}/Messages/translate-ids`,
        payload,
        {
          headers: getDefaultHeaders(),
        }
      );
      return data;
    },
  },
  attachments: {
    getUrl: async (messageId: string, attachmentId: string, forInvestorName?: string) => {
      const { data } = await axios.get<ApiResponse<FileInfo>>(
        `${baseUri}/Messages/${messageId}/attachments/${attachmentId}`,
        { headers: getDefaultHeaders(), params: { forInvestorName } }
      );
      return data;
    },
    getUrlForAll: async (messageIds: string[], forInvestorName?: string): Promise<string | undefined> => {
      const { data } = await axios.post<ApiResponse<{ token: string }>>(
        `${baseUri}/attachments/download`,
        {
          messageIds,
          forInvestorName,
        },
        { headers: getDefaultHeaders() }
      );
      return data?.success ? `${baseUri}/attachments/download?downloadToken=${data.data.token}` : undefined;
    },
  },
  inboxStructure: {
    load: async () => {
      const { data } = await axios.get<ApiResponse<InboxStructureTree>>(`${baseUri}/Inbox/Structure`, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
  },
  inboxUpdatesSubscription: {
    getToken: async () => {
      const { data } = await axios.get<ApiResponse<string>>(`${baseUri}/Inbox/updates-subscription-token`, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
  },
  fundraising: {
    getFundraising: async (id: string) => {
      const { data } = await axios.get<ApiResponse<Fundraising>>(`${baseUri}/Fundraisings/${id}`, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
    getFundraisingDocumentDownloadInfo: async (fundraisingId: string, documentId: string) => {
      const { data } = await axios.get<ApiResponse<FileInfo>>(
        `${baseUri}/Fundraisings/${fundraisingId}/documents/${documentId}`,
        { headers: getDefaultHeaders() }
      );

      return data;
    },
    getDownloadUrlForAllFundraisingDocuments: async (fundraisingId: string): Promise<ApiResponse<string>> => {
      const { data } = await axios.post<ApiResponse<{ token: string }>>(
        `${baseUri}/Fundraisings/${fundraisingId}/documents/download`,
        null,
        { headers: getDefaultHeaders() }
      );

      const downloadToken = data.data?.token;
      return downloadToken
        ? createApiResponse(
            `${baseUri}/Fundraisings/documents/download?${qs.stringify({ downloadToken, clientCode: getClientCode() })}`
          )
        : createErrorApiResponse(data.error);
    },
  },
  investorCommunications: {
    search: async (request: SearchInvestorCommunicationsRequest, signal?: AbortSignal) => {
      const { data } = await axios.post<ApiResponse<PaginatedList<InvestorCommunicationInfo>>>(
        `${baseUri}/InvestorCommunications/search`,
        request,
        { headers: getDefaultHeaders(), signal }
      );
      return data;
    },
    getInvestorCommunication: async (id: string, signal?: AbortSignal) => {
      const { data } = await axios.get<ApiResponse<InvestorCommunication>>(`${baseUri}/InvestorCommunications/${id}`, {
        headers: getDefaultHeaders(),
        signal,
      });
      return data;
    },
    getCategories: async (signal?: AbortSignal) => {
      const { data } = await axios.get<ApiResponse<InvestorCommunicationCategory[]>>(
        `${baseUri}/InvestorCommunications/categories`,
        { headers: getDefaultHeaders(), signal }
      );
      return data;
    },
  },
  widgets: {
    runPortfolioReport: async (payload: PortfolioReportRequest, signal?: AbortSignal) => {
      const { data } = await axios.post<ApiResponse<TabularReport>>(`${baseUri}/widgets/portfolio/report`, payload, {
        headers: getDefaultHeaders(),
        signal,
      });
      return data;
    },
    getPortfolioReportParameters: async () => {
      const { data } = await axios.get<ApiResponse<PortfolioReportParameters>>(
        `${baseUri}/widgets/portfolio/parameters`,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    runBalanceReport: async (request: BalanceReportRequest, signal?: AbortSignal) => {
      const { data } = await axios.post<ApiResponse<TabularReport>>(`${baseUri}/widgets/balance/report`, request, {
        headers: getDefaultHeaders(),
        signal,
      });
      return data;
    },
    exportBalanceReport: async (request: BalanceReportExportRequest): Promise<ApiResponse<string>> => {
      const { data } = await axios.post<ApiResponse<{ token: string }>>(`${baseUri}/widgets/balance/export`, request, {
        headers: getDefaultHeaders(),
      });

      const downloadToken = data.data?.token;
      return downloadToken
        ? createApiResponse(
            `${baseUri}/widgets/balance/export/download?${qs.stringify({ downloadToken, clientCode: getClientCode() })}`
          )
        : createErrorApiResponse(data.error);
    },
    getBalanceReportParameters: async () => {
      const { data } = await axios.get<ApiResponse<BalanceReportParameters>>(`${baseUri}/widgets/balance/parameters`, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
    runCashFlowsReport: async (request: CashFlowsReportRequest, signal?: AbortSignal) => {
      const { data } = await axios.post<ApiResponse<TabularReport>>(`${baseUri}/widgets/cash-flows/report`, request, {
        headers: getDefaultHeaders(),
        signal,
      });
      return data;
    },
    getCashFlowsReportParameters: async () => {
      const { data } = await axios.get<ApiResponse<CashFlowsReportParameters>>(
        `${baseUri}/widgets/cash-flows/parameters`,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
  },
  dataCollection: {
    searchDataSubmissions: async (payload: SearchDataCollectionRequest, signal?: AbortSignal) => {
      const { data } = await axios.post<ApiResponse<DataCollectionSubmissionInfo[]>>(
        `${baseUri}/DataSubmissions/search`,
        payload,
        { headers: getDefaultHeaders(), signal }
      );
      return data;
    },
    getDataSubmissionDetails: async (id: string, signal?: AbortSignal) => {
      const { data } = await axios.get<ApiResponse<DataCollectionSubmissionDetails>>(
        `${baseUri}/DataSubmissions/${id}`,
        { headers: getDefaultHeaders(), signal }
      );
      return data;
    },
    saveDataSubmissionLayoutBlockInput: async (
      submissionId: string,
      blockId: string,
      payload: SaveSubmissionLayoutBlockInputRequest
    ) => {
      const { data } = await axios.put<ApiResponse<DataCollectionSubmissionDetails>>(
        `${baseUri}/DataSubmissions/${submissionId}/blocks/${blockId}`,
        payload,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    submitDataSubmission: async (id: string) => {
      const { data } = await axios.post<ApiResponse<null>>(`${baseUri}/DataSubmissions/${id}/submit`, null, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
    exportSubmissionBlockInputToExcel: async (submissionId: string, blockId: string) => {
      const { data } = await axios.post<ApiResponse<FileDownloadInfo>>(
        `${baseUri}/DataSubmissions/${submissionId}/blocks/${blockId}/excel-export`,
        null,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
    importSubmissionBlockInputFromExcel: async (submissionId: string, blockId: string, file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axios.post<ApiResponse<ImportSubmissionBlockInputResponse>>(
        `${baseUri}/DataSubmissions/${submissionId}/blocks/${blockId}/excel-import`,
        formData,
        { headers: getDefaultHeaders({ formData: true }) }
      );
      return data;
    },
    searchObjectMetricExtensions: async (payload: SearchObjectMetricExtensionsRequest) => {
      const { data } = await axios.post<ApiResponse<ObjectMetricExtension[]>>(
        `${baseUri}/ObjectMetricExtensions/search`,
        payload,
        {
          headers: getDefaultHeaders(),
        }
      );
      return data;
    },
    createObjectMetricExtensions: async (payload: CreateMultipleObjectMetricExtensionsRequest) => {
      const { data } = await axios.post<ApiResponse<ObjectMetricExtension[]>>(
        `${baseUri}/ObjectMetricExtensions/create-multiple`,
        payload,
        { headers: getDefaultHeaders() }
      );
      return data;
    },
  },
  troubleshooting: {
    getBlobProbes: async () => {
      const { data } = await axios.get<ApiResponse<BlobProbeTarget[]>>(`${baseUri}/Troubleshooting/blob-probes`, {
        headers: getDefaultHeaders(),
      });
      return data;
    },
  },
};
